import * as XLSX from 'xlsx';

// ── Helper interno ─────────────────────────────────────────────────────────
function num(v) { return parseFloat(v) || 0; }

function descargar(wb, nombre) {
  XLSX.writeFile(wb, `${nombre}.xlsx`);
}

function autoWidth(ws) {
  const ref = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  const cols = [];
  for (let c = ref.s.c; c <= ref.e.c; c++) {
    let max = 10;
    for (let r = ref.s.r; r <= ref.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.v != null) {
        const len = String(cell.v).length;
        if (len > max) max = len;
      }
    }
    cols.push({ wch: Math.min(max + 2, 50) });
  }
  ws['!cols'] = cols;
}

function hoja(datos, titulo) {
  const ws = XLSX.utils.aoa_to_sheet(datos);
  autoWidth(ws);
  return { ws, titulo };
}

// ── Ventas ─────────────────────────────────────────────────────────────────
export function exportarVentas(data, desde, hasta) {
  const { resumen, por_dia, por_forma_pago, top_productos, top_clientes } = data;
  const wb = XLSX.utils.book_new();
  const periodo = desde === hasta ? desde : `${desde} al ${hasta}`;

  // Resumen
  const { ws: wsRes } = hoja([
    ['Reporte de Ventas', '', periodo],
    [],
    ['Métrica', 'Valor'],
    ['Total facturado',       num(resumen.total)],
    ['Facturas emitidas',     num(resumen.cantidad)],
    ['Ticket promedio',       num(resumen.ticket_promedio)],
    ['Descuentos otorgados',  num(resumen.descuentos)],
    ['Neto (sin descuentos)', num(resumen.total) - num(resumen.descuentos)],
  ], 'Resumen');
  XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');

  // Por día
  const { ws: wsDia } = hoja([
    ['Fecha', 'Cantidad de ventas', 'Total ($)'],
    ...(por_dia ?? []).map(d => [d.dia, num(d.cantidad), num(d.total)]),
  ], 'Por día');
  XLSX.utils.book_append_sheet(wb, wsDia, 'Por día');

  // Por forma de pago
  const { ws: wsFP } = hoja([
    ['Forma de pago', 'Cantidad', 'Total ($)'],
    ...(por_forma_pago ?? []).map(fp => [fp.forma_pago, num(fp.cantidad), num(fp.total)]),
  ], 'Por forma de pago');
  XLSX.utils.book_append_sheet(wb, wsFP, 'Forma de pago');

  // Top productos
  const { ws: wsProd } = hoja([
    ['Producto', 'Cantidad vendida', 'Total ($)'],
    ...(top_productos ?? []).map(p => [p.nombre, num(p.cantidad), num(p.total)]),
  ], 'Top productos');
  XLSX.utils.book_append_sheet(wb, wsProd, 'Top productos');

  // Top clientes (solo si hay datos reales)
  const clientesReales = (top_clientes ?? []).filter(c => c.cliente !== 'Sin cliente');
  if (clientesReales.length) {
    const { ws: wsCli } = hoja([
      ['Cliente', 'Cantidad de ventas', 'Total ($)'],
      ...clientesReales.map(c => [c.cliente, num(c.cantidad), num(c.total)]),
    ], 'Top clientes');
    XLSX.utils.book_append_sheet(wb, wsCli, 'Top clientes');
  }

  descargar(wb, `Ventas_${desde}_${hasta}`);
}

// ── Compras ────────────────────────────────────────────────────────────────
export function exportarCompras(data, desde, hasta) {
  const { resumen, por_dia, top_proveedores, top_productos } = data;
  const wb = XLSX.utils.book_new();

  const { ws: wsRes } = hoja([
    ['Reporte de Compras', '', `${desde} al ${hasta}`],
    [],
    ['Métrica', 'Valor'],
    ['Total comprado',         num(resumen.total)],
    ['Facturas de compra',     num(resumen.cantidad)],
    ['Promedio por factura',   num(resumen.promedio)],
  ], 'Resumen');
  XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');

  const { ws: wsDia } = hoja([
    ['Fecha', 'Cantidad de compras', 'Total ($)'],
    ...(por_dia ?? []).map(d => [d.dia, num(d.cantidad), num(d.total)]),
  ], 'Por día');
  XLSX.utils.book_append_sheet(wb, wsDia, 'Por día');

  const { ws: wsProv } = hoja([
    ['Proveedor', 'Cantidad de facturas', 'Total ($)'],
    ...(top_proveedores ?? []).map(p => [p.proveedor, num(p.cantidad), num(p.total)]),
  ], 'Top proveedores');
  XLSX.utils.book_append_sheet(wb, wsProv, 'Top proveedores');

  const { ws: wsProd } = hoja([
    ['Producto', 'Cantidad comprada', 'Total ($)'],
    ...(top_productos ?? []).map(p => [p.nombre, num(p.cantidad), num(p.total)]),
  ], 'Top productos');
  XLSX.utils.book_append_sheet(wb, wsProd, 'Top productos');

  descargar(wb, `Compras_${desde}_${hasta}`);
}

// ── Stock ──────────────────────────────────────────────────────────────────
export function exportarStock(data) {
  const { resumen, productos } = data;
  const wb = XLSX.utils.book_new();
  const hoy = new Date().toISOString().slice(0, 10);

  const { ws: wsRes } = hoja([
    ['Reporte de Stock', '', hoy],
    [],
    ['Métrica', 'Valor'],
    ['Total productos activos',    num(resumen.total_productos)],
    ['Valor a precio de costo ($)', num(resumen.valor_costo)],
    ['Valor a precio de venta ($)', num(resumen.valor_venta)],
    ['Productos con stock crítico', num(resumen.bajo_stock)],
  ], 'Resumen');
  XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');

  const { ws: wsProds } = hoja([
    ['Producto', 'SKU', 'Categoría', 'Unidad', 'Stock actual', 'Stock mínimo',
     'Precio costo ($)', 'Precio venta ($)', 'Valor a costo ($)', 'Valor a venta ($)', 'Estado'],
    ...(productos ?? []).map(p => {
      const sinStock = p.controla_stock && p.stock_actual <= 0;
      const bajo     = p.controla_stock && p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo && p.stock_actual > 0;
      const estado   = !p.controla_stock ? 'Sin control' : sinStock ? 'Sin stock' : bajo ? 'Bajo mínimo' : 'OK';
      return [
        p.nombre,
        p.sku || '',
        p.categoria,
        p.unidad,
        num(p.stock_actual),
        p.controla_stock && p.stock_minimo > 0 ? num(p.stock_minimo) : '',
        num(p.precio_costo),
        num(p.precio_venta),
        num(p.valor_costo),
        num(p.valor_venta),
        estado,
      ];
    }),
  ], 'Inventario');
  XLSX.utils.book_append_sheet(wb, wsProds, 'Inventario');

  descargar(wb, `Stock_${hoy}`);
}

// ── Rentabilidad ───────────────────────────────────────────────────────────
export function exportarRentabilidad(data, desde, hasta) {
  const { resumen, productos } = data;
  const wb = XLSX.utils.book_new();

  const { ws: wsRes } = hoja([
    ['Reporte de Rentabilidad', '', `${desde} al ${hasta}`],
    [],
    ['Métrica', 'Valor'],
    ['Ingresos totales ($)',  num(resumen.ingresos)],
    ['Costos totales ($)',    num(resumen.costos)],
    ['Ganancia bruta ($)',    num(resumen.ganancia)],
    ['Margen %',             num(resumen.margen_pct)],
  ], 'Resumen');
  XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen');

  const { ws: wsProds } = hoja([
    ['Producto', 'Unidades vendidas', 'Ingresos ($)', 'Costos ($)', 'Ganancia ($)', 'Margen %'],
    ...(productos ?? []).map(p => [
      p.nombre,
      num(p.unidades_vendidas),
      num(p.ingresos),
      num(p.costos),
      num(p.ganancia),
      num(p.margen_pct),
    ]),
  ], 'Detalle');
  XLSX.utils.book_append_sheet(wb, wsProds, 'Detalle por producto');

  descargar(wb, `Rentabilidad_${desde}_${hasta}`);
}
